import React, { useState, useEffect, useMemo } from "react";
import DateRangePicker from 'react-bootstrap-daterangepicker';
import '../assets/styles/stylesheets/daterangepicker.css';
import moment from "moment";
import axiosInstance from "../Services/axiosInstance";
import {
    Box, Grid, MenuItem, Select, Typography, Breadcrumbs, TextField,
    ToggleButton, ToggleButtonGroup, Paper, useMediaQuery, useTheme,
} from "@mui/material";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, Rectangle, ResponsiveContainer, CartesianGrid } from 'recharts';
import CustomizedSnackbars from '../utils/snackbar.js';
import Loader from '../utils/Loader.js';
import { ReactComponent as BacktoJob } from "../assets/images/back-to-jobs-black.svg";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router";
import { getErrorMessage } from "../utils/helper.js";
import { danger } from '../utils/message-box.js';

const PLATFORM_BAR_KEYS = ['PBP', 'PSP', 'BH', 'OF', 'AM', 'JM', 'FK', 'TD', 'AB'];

const PLATFORM_BAR_COLORS = {
    PBP: '#1f77b4', PSP: '#ff7f0e', BH: '#2ca02c', OF: '#d62728',
    AM: '#9467bd', JM: '#8c564b', FK: '#e377c2', TD: '#7f7f7f', AB: '#bcbd22',
};

const sumChartRows = (rows) => rows.reduce((total, row) => {
    const rowSum = Object.entries(row).reduce((sum, [key, value]) => {
        if (key === 'month' || key === 'size') return sum;
        return sum + (Number(value) || 0);
    }, 0);
    return total + rowSum;
}, 0);

const SIZE_SALES_COLORS = {
    B: '#8884d8',
    SB: '#82ca9d',
    FG: '#ffc658',
    '2FG': '#ff7300',
    OR: '#ff8042',
    WF: '#0088fe',
    PR: '#00C49F',
    SPS: '#a4de6c',
};

const getMobileChartWidth = (dataLength, minPointWidth = 88) => (
    Math.max(300, (dataLength || 1) * minPointWidth)
);

const ChartScrollWrap = ({ dataLength, isMobile, height, className = '', children }) => {
    const scrollWidth = getMobileChartWidth(dataLength);

    if (!isMobile) {
        return (
            <Box className={`analytics-chart-wrap ${className}`.trim()} sx={{ height }}>
                <Box className="analytics-chart-scroll-inner" sx={{ width: '100%', height: '100%' }}>
                    {children}
                </Box>
            </Box>
        );
    }

    return (
        <Box className={`analytics-chart-wrap ${className}`.trim()}>
            <Box className="analytics-chart-scroll" sx={{ maxWidth: '100%' }}>
                <Box
                    className="analytics-chart-scroll-inner"
                    sx={{ width: scrollWidth, minWidth: scrollWidth, height }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

const Benfit = () => {
    const calculateAmounts = (data) => {
        let totalPostOfficeAmount = 0;
        let totalPortalAmount = 0;
        let totalNewPortalAmount = 0;

        data.forEach((record) => {
            let postOfficeAmount = 0;
            let portalAmount = 0;
            let NewPortalAmount = 0;

            // Post Office Amount Calculation
            if (
                ["PBP", "PSP", "BH", "OF"].includes(record.business_type) &&
                ["Dispatched", "Delivered"].includes(record.order_status)
            ) {
                const delivaryCharges = parseFloat(record.delivary_charges) || 0;
                let totalCost = 0;
                record.size_details.forEach((sizeDetail) => {
                    const { product_type, quantity } = sizeDetail;
                    if (product_type === "B")
                        totalCost += quantity * 710
                    else if (product_type === "SB")
                        totalCost += quantity * 670
                    else if (product_type === "OR")
                        totalCost += quantity * 980
                    else if (product_type === "FG")
                        totalCost += quantity * 800
                    else if (product_type === "2FG")
                        totalCost += quantity * 900
                    else if (product_type === "WF")
                        totalCost += quantity * 1960
                });
                totalCost += delivaryCharges;

                const remainingAmount = totalCost - (record.cod + record.prepaid);
                totalPostOfficeAmount += Math.abs(remainingAmount);

                return totalPostOfficeAmount;
            }

            if (
                ["PBP", "PSP", "BH", "OF"].includes(record.business_type) &&
                record.order_status === "Returned"
            ) {
                postOfficeAmount -= parseFloat(record.delivary_charges) || 0;
            }

            totalPostOfficeAmount += postOfficeAmount;

            // Portal Amount Calculation
            if (["AM", "JM", "FK"].includes(record.business_type)) {
                if (record.order_status === "Dispatched") {
                    let totalPrice = 0;
                    record.size_details.forEach((sizeDetail) => {
                        const { product_type, quantity } = sizeDetail;
                        if (product_type === "B")
                            totalPrice += (quantity * 1175) - (quantity * 710)
                        else if (product_type === "SB")
                            totalPrice += (quantity * 1175) - (quantity * 670)
                        else if (product_type === "OR")
                            totalPrice += (quantity * 1175) - (quantity * 980)
                        else if (product_type === "FG")
                            totalPrice += (quantity * 1900) - (quantity * 800)
                        else if (product_type === "2FG")
                            totalPrice += (quantity * 1800) - (quantity * 900)
                        else if (product_type === "WF")
                            totalPrice += (quantity * 0) - (quantity * 1960)
                    });
                    totalPortalAmount += totalPrice;
                }

                if (record.order_status === "Returned") {
                    let totalReturnPrice = 0;
                    record.size_details.forEach((sizeDetail) => {
                        const { product_type, quantity } = sizeDetail;
                        if (product_type === "B")
                            totalReturnPrice += quantity * 565
                        else if (product_type === "SB")
                            totalReturnPrice += quantity * 605
                        else if (product_type === "OR")
                            totalReturnPrice += quantity * 295
                        else if (product_type === "FG")
                            totalReturnPrice += quantity * 1200
                        else if (product_type === "2FG")
                            totalReturnPrice += quantity * 1000
                        else if (product_type === "WF")
                            totalReturnPrice += quantity * 0
                    });
                    portalAmount -= totalReturnPrice;
                }
                totalPortalAmount += portalAmount;
            }
            if (["TD", "AB", "PF1", "PF2"].includes(record.business_type)) {
                if (record.order_status === "Dispatched") {
                    let totalPrice = 0;
                    record.size_details.forEach((sizeDetail) => {
                        const { product_type, quantity } = sizeDetail;
                        if (product_type === "B")
                            totalPrice += (quantity * 1118) - (quantity * 710)
                        else if (product_type === "SB")
                            totalPrice += (quantity * 1118) - (quantity * 670)
                        else if (product_type === "OR")
                            totalPrice += (quantity * 1118) - (quantity * 980)
                        else if (product_type === "FG")
                            totalPrice += (quantity * 1770) - (quantity * 800)
                        else if (product_type === "2FG")
                            totalPrice += (quantity * 1770) - (quantity * 900)
                        else if (product_type === "WF")
                            totalPrice += (quantity * 0) - (quantity * 1960)
                    });
                    totalNewPortalAmount += totalPrice;
                }

                if (record.order_status === "Returned") {
                    let totalReturnPrice = 0;
                    record.size_details.forEach((sizeDetail) => {
                        const { product_type, quantity } = sizeDetail;
                        if (product_type === "B")
                            totalReturnPrice += quantity * 408
                        else if (product_type === "SB")
                            totalReturnPrice += quantity * 448
                        else if (product_type === "OR")
                            totalReturnPrice += quantity * 138
                        else if (product_type === "FG")
                            totalReturnPrice += quantity * 970
                        else if (product_type === "2FG")
                            totalReturnPrice += quantity * 870
                        else if (product_type === "WF")
                            totalReturnPrice += quantity * 0
                    });
                    NewPortalAmount -= totalReturnPrice;
                }
                totalNewPortalAmount += NewPortalAmount;
            }
        });
        return totalPostOfficeAmount + totalPortalAmount + totalNewPortalAmount;
    };
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const chartHeight = isMobile ? 260 : 280;
    const tallChartHeight = isMobile ? 300 : 360;
    const chartMargin = isMobile
        ? { top: 8, right: 8, left: -8, bottom: 4 }
        : { top: 8, right: 24, left: 8, bottom: 8 };
    const xAxisMonthProps = isMobile
        ? { angle: -35, textAnchor: 'end', height: 56, interval: 0 }
        : { angle: 0, textAnchor: 'middle', height: 30, interval: 'preserveStartEnd' };
    const tickStyle = { fill: '#667085', fontSize: isMobile ? 10 : 12 };
    const barSize = isMobile ? 18 : 28;
    const [isLoading, setIsLoading] = useState(true);
    const [totalAmount, setTotalAmount] = useState(null);
    const [benefitLoaded, setBenefitLoaded] = useState(false);
    const defaultDateRange = { start: moment().subtract(29, 'days'), end: moment() };
    const [dateRange, setDateRange] = useState(null);
    const pickerStart = dateRange?.start ?? defaultDateRange.start;
    const pickerEnd = dateRange?.end ?? defaultDateRange.end;
    const label = benefitLoaded && dateRange
        ? `${dateRange.start.format('MMM D, YYYY')} - ${dateRange.end.format('MMM D, YYYY')}`
        : 'Select date range';
    const [chartData, setChartData] = useState([]);
    const [platformData, setPlatformData] = useState([]);
    const [platformReturnsData, setPlatformReturnsData] = useState([]);
    const [sizeSalesChartData, setSizeSalesChartData] = useState([]);
    const [sizeSalesProductTypes, setSizeSalesProductTypes] = useState([]);
    const [analyticsView, setAnalyticsView] = useState('year');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
    const [periodLabel, setPeriodLabel] = useState(String(new Date().getFullYear()));

    const groupByMonth = (data) => {
        const grouped = {};

        data.forEach(record => {
            const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];
            const date = new Date(record.created_at);
            const monthKey = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            // const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // e.g., "2025-04"
            if (!grouped[monthKey]) {
                grouped[monthKey] = [];
            }
            grouped[monthKey].push(record);
        });

        return grouped;
    };
    const groupByMonthAndBusinessType = (data) => {
        const grouped = {};

        data.forEach(record => {
            const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];
            const date = new Date(record.created_at);
            const monthKey = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            const businessType = record.business_type;
            // const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // e.g., "2025-04"
            const key = `${monthKey}_${businessType}`;
            if (!grouped[key]) {
                grouped[key] = {
                    month: monthKey,
                    business_type: businessType,
                    records: []
                };
            }
            grouped[key].records.push(record);
        });

        return Object.values(grouped);
    };
    const generateMonthlyBenefitData = (records) => {
        const grouped = groupByMonth(records);
        return Object.entries(grouped).map(([month, monthlyRecords]) => ({
            month,
            orders: monthlyRecords.reduce((total, item) => total + (item?.order_status !== "Returned" ? item.order_quantity : 0), 0),
        }));
    };
    const generatePlatformBenefitData = (records) => {
        const groupedData = groupByMonthAndBusinessType(records);

        return groupedData.map(group => ({
            month: group.month,
            business_type: group.business_type,
            orders: group.records?.reduce((total, item) => total + (item?.order_status !== "Returned" ? item.order_quantity : 0), 0)
        }));
    };
    const transformForBarChart = (data) => {
        const result = {};

        data.forEach(({ month, business_type, orders }) => {
            if (!result[month]) {
                result[month] = { month };
            }
            result[month][business_type] = orders;
        });

        return Object.values(result);
    };
    // useEffect(() => {
        // const amount = calculateAmounts(recordsList);
        // setTotalAmount(amount);
        // setChartData(generateMonthlyBenefitData(recordsList));
        // setPlatformData(transformForBarChart(generatePlatformBenefitData(recordsList)));
    // }, [recordsList]);
    useEffect(() => {
        getOrdersData();
    }, [analyticsView, selectedYear, selectedMonth]);

    const totalOrders = useMemo(
        () => chartData.reduce((sum, item) => sum + (Number(item.orders) || 0), 0),
        [chartData]
    );
    const totalDeliveredUnits = useMemo(() => sumChartRows(platformData), [platformData]);
    const totalReturns = useMemo(() => sumChartRows(platformReturnsData), [platformReturnsData]);

    const getOrdersData = () => {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (analyticsView === 'month') {
            const [year, month] = selectedMonth.split('-');
            params.set('year', year);
            params.set('month', String(parseInt(month, 10)));
        } else {
            params.set('year', String(selectedYear));
        }

        axiosInstance.get(`/api/v1/customers/analytics-data?${params.toString()}`)
            .then((res) => {
                setChartData(res.data.monthly_benefit_data);
                setPlatformData(res.data.bar_chart_data);
                setPlatformReturnsData(res.data.returns_bar_chart_data);
                setSizeSalesChartData(res.data.size_sales_chart_data || []);
                setSizeSalesProductTypes(res.data.size_sales_product_types || []);
                setPeriodLabel(res.data.period_label || String(selectedYear));
                setIsLoading(false);
            }).catch((e) => {
                setIsLoading(false);
                danger(getErrorMessage(e, 'Failed to load analytics data.'));
            });
    };
    const handleCallback = (start, end) => {
        setIsLoading(true);
        const range = { start: moment(start), end: moment(end) };
        setDateRange(range);
        const start_date = range.start.format('YYYY-MM-DD');
        const end_date = range.end.format('YYYY-MM-DD');
        const url = `/api/v1/customers/benefit-data?start_date=${start_date}&end_date=${end_date}`;

        axiosInstance.get(url)
            .then((res) => {
                setTotalAmount(res.data.total_benefit);
                setBenefitLoaded(true);
                setIsLoading(false);
            }).catch((e) => {
                setIsLoading(false);
                danger(getErrorMessage(e, 'Something went wrong.'));
            });
    };
    const handleClickOnCancel = () => {
        setDateRange(null);
        setTotalAmount(null);
        setBenefitLoaded(false);
    };

    const renderPlatformBars = () => PLATFORM_BAR_KEYS.map((key) => (
        <Bar
            key={key}
            dataKey={key}
            fill={PLATFORM_BAR_COLORS[key]}
            activeBar={<Rectangle fill={`${PLATFORM_BAR_COLORS[key]}88`} stroke={PLATFORM_BAR_COLORS[key]} />}
        />
    ));

    const showOrdersTrendChart = analyticsView === 'year' && chartData.length > 0;

    return (
        <>
            {isLoading && <Loader />}
            <Box className="analytics-dashboard">
                <Typography>
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        className="pt-0 pb-10"
                        separator={<NavigateNextIcon fontSize="small" aria-label="Next icon" />}
                    >
                        <span
                            className="cursor-hover d-flex f-12 gray9 font-weight-600 text-decoration-none gal-slot-align"
                            onClick={() => navigate('/home')}
                        >
                            <BacktoJob className="mr-10" />
                            Back to Home
                        </span>
                    </Breadcrumbs>
                </Typography>

                <Box className="analytics-dashboard-header">
                    <Typography className="analytics-dashboard-title">Analytics Dashboard</Typography>
                    <Typography className="analytics-dashboard-subtitle">
                        Track order volume and platform performance
                    </Typography>
                </Box>

                <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper elevation={0} className="analytics-benefit-card">
                            <Box>
                                <Typography className="analytics-benefit-label">Aggregate Value</Typography>
                                <Typography className="analytics-benefit-value">
                                    {benefitLoaded
                                        ? `₹${Number(totalAmount ?? 0).toFixed(2).toLocaleString('en-IN')}`
                                        : '—'}
                                </Typography>
                                {!benefitLoaded && (
                                    <Typography className="analytics-kpi-hint">
                                        Select a date range below to aggregate Value
                                    </Typography>
                                )}
                            </Box>
                            <Box className="analytics-benefit-range bluk-upload-daterangepicker">
                                <DateRangePicker
                                    initialSettings={{
                                        startDate: pickerStart.toDate(),
                                        endDate: pickerEnd.toDate(),
                                        ranges: {
                                            Today: [moment().toDate(), moment().toDate()],
                                            Yesterday: [
                                                moment().subtract(1, 'days').toDate(),
                                                moment().subtract(1, 'days').toDate(),
                                            ],
                                            'Last 7 Days': [
                                                moment().subtract(6, 'days').toDate(),
                                                moment().toDate(),
                                            ],
                                            'Last 30 Days': [
                                                moment().subtract(29, 'days').toDate(),
                                                moment().toDate(),
                                            ],
                                            'This Month': [
                                                moment().startOf('month').toDate(),
                                                moment().endOf('month').toDate(),
                                            ],
                                            'Last Month': [
                                                moment().subtract(1, 'month').startOf('month').toDate(),
                                                moment().subtract(1, 'month').endOf('month').toDate(),
                                            ],
                                        },
                                    }}
                                    onCallback={handleCallback}
                                    onCancel={handleClickOnCancel}
                                >
                                    <div className="analytics-date-trigger">
                                        <i className="fa fa-calendar" aria-hidden="true" />
                                        <span className="analytics-date-trigger-label">{label}</span>
                                        <i className="fa fa-caret-down" aria-hidden="true" />
                                    </div>
                                </DateRangePicker>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper elevation={0} className="analytics-toolbar">
                    <Box className="analytics-toolbar-left">
                        <Typography className="analytics-toolbar-heading">Order Analytics</Typography>
                        <Typography className="analytics-toolbar-desc">
                            {analyticsView === 'month'
                                ? 'Monthly totals and platform breakdown'
                                : 'Monthly trends across the selected year'}
                        </Typography>
                    </Box>
                    <Box className="analytics-toolbar-right">
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={analyticsView}
                            className="analytics-view-toggle"
                            onChange={(_, value) => {
                                if (value) setAnalyticsView(value);
                            }}
                        >
                            <ToggleButton value="year">Yearly</ToggleButton>
                            <ToggleButton value="month">Monthly</ToggleButton>
                        </ToggleButtonGroup>
                        {analyticsView === 'year' ? (
                            <Select
                                id="analytics-year"
                                size="small"
                                className="analytics-period-select form-control input-group mb-0 campain"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                            >
                                {Array.from({ length: new Date().getFullYear() - 2023 + 1 }, (_, i) => {
                                    const year = 2023 + i;
                                    return (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        ) : (
                            <TextField
                                type="month"
                                size="small"
                                fullWidth={isMobile}
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                className="analytics-period-select form-control input-group mb-0 inputcontrasts"
                                sx={{ width: isMobile ? '100%' : 170 }}
                            />
                        )}
                        {/* <Chip label={periodLabel} className="analytics-period-chip" size="small" /> */}
                    </Box>
                </Paper>

                <Grid container spacing={2} className="analytics-kpi-grid">
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} className="analytics-kpi-card analytics-kpi-card--orders">
                            <Box className="analytics-kpi-card-header">
                                <Typography className="analytics-kpi-label">Delivered / Dispatched</Typography>
                                <Box className="analytics-kpi-icon"><ShoppingCartOutlinedIcon /></Box>
                            </Box>
                            <Typography className="analytics-kpi-value">{totalOrders.toLocaleString('en-IN')}</Typography>
                            <Typography className="analytics-kpi-hint">
                                {analyticsView === 'month'
                                    ? `Delivered & Dispatched in ${periodLabel}`
                                    : `Delivered & Dispatched across ${periodLabel}`}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} className="analytics-kpi-card analytics-kpi-card--delivered">
                            <Box className="analytics-kpi-card-header">
                                <Typography className="analytics-kpi-label">Delivered / Dispatched Units</Typography>
                                <Box className="analytics-kpi-icon"><LocalShippingOutlinedIcon /></Box>
                            </Box>
                            <Typography className="analytics-kpi-value">{totalDeliveredUnits.toLocaleString('en-IN')}</Typography>
                            <Typography className="analytics-kpi-hint">Delivered or Dispatched, by business type</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} className="analytics-kpi-card analytics-kpi-card--returns">
                            <Box className="analytics-kpi-card-header">
                                <Typography className="analytics-kpi-label">Returns</Typography>
                                <Box className="analytics-kpi-icon"><ReplayOutlinedIcon /></Box>
                            </Box>
                            <Typography className="analytics-kpi-value">{totalReturns.toLocaleString('en-IN')}</Typography>
                            <Typography className="analytics-kpi-hint">Returned order quantity in period</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} className="analytics-kpi-card analytics-kpi-card--period">
                            <Box className="analytics-kpi-card-header">
                                <Typography className="analytics-kpi-label">Selected Period</Typography>
                                <Box className="analytics-kpi-icon"><CalendarMonthOutlinedIcon /></Box>
                            </Box>
                            <Typography className="analytics-kpi-value" sx={{ fontSize: '20px !important' }}>
                                {periodLabel}
                            </Typography>
                            <Typography className="analytics-kpi-hint">
                                {analyticsView === 'month' ? 'Monthly view' : 'Yearly view'}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {showOrdersTrendChart && (
                    <Paper elevation={0} className="analytics-chart-card">
                        <Typography className="analytics-chart-card-title">Orders by Month</Typography>
                        <Typography className="analytics-chart-card-subtitle">
                            Delivered & Dispatched order quantity trend for {periodLabel}
                        </Typography>
                        <ChartScrollWrap dataLength={chartData.length} isMobile={isMobile} height={chartHeight}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={chartMargin}
                                    barSize={barSize}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaecf0" />
                                    <XAxis
                                        dataKey="month"
                                        tick={tickStyle}
                                        axisLine={false}
                                        tickLine={false}
                                        {...xAxisMonthProps}
                                    />
                                    <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={isMobile ? 32 : 40} />
                                    <Tooltip />
                                    <Bar dataKey="orders" fill="#175cd3" radius={[6, 6, 0, 0]} background={{ fill: '#f2f4f7' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartScrollWrap>
                    </Paper>
                )}

                <Paper elevation={0} className="analytics-chart-card">
                    <Typography className="analytics-chart-card-title">Delivered &amp; Dispatched Data</Typography>
                    <Typography className="analytics-chart-card-subtitle">
                        Delivered & Dispatched order quantity by business type — {periodLabel}
                    </Typography>
                    {platformData?.length ? (
                        <ChartScrollWrap dataLength={platformData.length} isMobile={isMobile} height={chartHeight}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={platformData} margin={chartMargin}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaecf0" />
                                    <XAxis
                                        dataKey="month"
                                        tick={tickStyle}
                                        axisLine={false}
                                        tickLine={false}
                                        {...xAxisMonthProps}
                                    />
                                    <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={isMobile ? 32 : 40} />
                                    <Tooltip />
                                    {renderPlatformBars()}
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartScrollWrap>
                    ) : (
                        <Box className="analytics-empty-state">No delivered data for this period</Box>
                    )}
                </Paper>

                <Paper elevation={0} className="analytics-chart-card">
                    <Typography className="analytics-chart-card-title">Returned Data</Typography>
                    <Typography className="analytics-chart-card-subtitle">
                        Returned order quantity by business type — {periodLabel}
                    </Typography>
                    {platformReturnsData?.length ? (
                        <ChartScrollWrap dataLength={platformReturnsData.length} isMobile={isMobile} height={chartHeight}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={platformReturnsData} margin={chartMargin}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaecf0" />
                                    <XAxis
                                        dataKey="month"
                                        tick={tickStyle}
                                        axisLine={false}
                                        tickLine={false}
                                        {...xAxisMonthProps}
                                    />
                                    <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={isMobile ? 32 : 40} />
                                    <Tooltip />
                                    {renderPlatformBars()}
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartScrollWrap>
                    ) : (
                        <Box className="analytics-empty-state">No return data for this period</Box>
                    )}
                </Paper>

                <Paper elevation={0} className="analytics-chart-card">
                    <Typography className="analytics-chart-card-title">Product Type &amp; Size Sales</Typography>
                    <Typography className="analytics-chart-card-subtitle">
                        Quantity sold by size across each product type — {periodLabel} (Delivered, Dispatched &amp; In-transit)
                    </Typography>
                    {sizeSalesChartData?.length ? (
                        <ChartScrollWrap
                            dataLength={sizeSalesChartData.length}
                            isMobile={isMobile}
                            height={tallChartHeight}
                            className="analytics-chart-wrap--tall"
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={sizeSalesChartData} margin={chartMargin}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eaecf0" />
                                    <XAxis dataKey="size" tick={tickStyle} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={tickStyle} axisLine={false} tickLine={false} width={isMobile ? 32 : 40} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 13, paddingTop: 8 }} />
                                    {sizeSalesProductTypes.map((productType) => (
                                        <Line
                                            key={productType}
                                            type="monotone"
                                            dataKey={productType}
                                            stroke={SIZE_SALES_COLORS[productType] || '#333'}
                                            strokeWidth={isMobile ? 1.5 : 2}
                                            dot={{ r: isMobile ? 3 : 4 }}
                                            connectNulls
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartScrollWrap>
                    ) : (
                        <Box className="analytics-empty-state">No size sales data for this period</Box>
                    )}
                </Paper>
            </Box>
        </>
    );
};

export default Benfit;
